import { useEffect, useRef } from 'react';

/**
 * SignalGrid — signal → system → outcome
 *
 * 5 nodes · 2 path pulses · gradient trail
 * Node hierarchy: 1 primary (hub), 2 secondary, 2 tertiary
 * Desktop: full system · Mobile: 3-node core path
 */

const NODE_DEFS = [
  { xp:  7, yp: 48, tier: 'primary'   },  // 0  left anchor / hub
  { xp: 34, yp:  7, tier: 'secondary' },  // 1  upper bridge
  { xp: 84, yp: 11, tier: 'secondary' },  // 2  upper right (above cards)
  { xp: 93, yp: 79, tier: 'tertiary'  },  // 3  lower right
  { xp: 46, yp: 89, tier: 'tertiary'  },  // 4  lower centre
];

const CONNECTIONS_D = [[0, 1], [1, 2], [2, 3], [3, 4], [0, 4]];
const CONNECTIONS_M = [[0, 1], [1, 2]];

// Path-based pulses — each travels through a chained sequence of nodes
// speed: progress units per ms  (1/speed = cycle duration in ms)
const PATH_PULSE_DEFS = [
  { path: [0, 1, 2],    t0: 0.08, speed: 0.000044, rgb: [155, 63, 254] }, // violet  ~23 s/cycle
  { path: [1, 2, 3, 4], t0: 0.58, speed: 0.000030, rgb: [0, 229, 255]  }, // cyan    ~33 s/cycle
];

// Tier visual parameters
const TIER = {
  primary:   { ring: 7,   core: 8,   ringA: 0.13, coreA: 0.62, breathe: 0.28 },
  secondary: { ring: 4.5, core: 5.5, ringA: 0.08, coreA: 0.46, breathe: 0.20 },
  tertiary:  { ring: 3,   core: 3.5, ringA: 0.05, coreA: 0.28, breathe: 0.13 },
};

const SignalGrid = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finePtr = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const ctx     = canvas.getContext('2d');

    let nodes = [], conns = [], lw = 0, lh = 0, dpr = 1, mobile = false;

    // Per-node smooth state indexed by NODE_DEFS index (di)
    const hoverG = Array(NODE_DEFS.length).fill(0);
    const pulseG = Array(NODE_DEFS.length).fill(0);

    // Mutable pulse progress
    const pState = PATH_PULSE_DEFS.map(p => ({ t: p.t0 }));

    // Mouse / parallax state
    const rawMouse = { x: -9999, y: -9999 };
    const pxTgt   = { x: 0, y: 0 };
    const pxCur   = { x: 0, y: 0 };

    let raf = null;

    /* ── Resize ──────────────────────────────────────────────── */
    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      lw  = canvas.offsetWidth;
      lh  = canvas.offsetHeight;
      if (!lw || !lh) return;
      canvas.width  = lw * dpr;
      canvas.height = lh * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      mobile = lw < 768;
      const sub = mobile ? [0, 1, 2] : [0, 1, 2, 3, 4];
      nodes = sub.map(i => ({
        x:  (NODE_DEFS[i].xp / 100) * lw,
        y:  (NODE_DEFS[i].yp / 100) * lh,
        tier: NODE_DEFS[i].tier,
        di: i,
      }));
      conns = mobile ? CONNECTIONS_M : CONNECTIONS_D;
    };

    /* ── Mouse ───────────────────────────────────────────────── */
    const onMouse = e => {
      rawMouse.x = e.clientX;
      rawMouse.y = e.clientY;
      if (finePtr && !reduced) {
        pxTgt.x = ((e.clientX - lw / 2) / (lw / 2)) * 6;
        pxTgt.y = ((e.clientY - lh / 2) / (lh / 2)) * 4;
      }
    };

    /* ── Draw helpers ────────────────────────────────────────── */
    const drawLine = (x1, y1, x2, y2) => {
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.strokeStyle = 'rgba(255,255,255,0.048)';
      ctx.lineWidth   = 0.6;
      ctx.stroke();
    };

    // Gradient trail: fades from transparent at tail to coloured at pulse head
    const drawTrail = (x1, y1, x2, y2, r, g, b, opacity) => {
      const tg = ctx.createLinearGradient(x1, y1, x2, y2);
      tg.addColorStop(0, `rgba(${r},${g},${b},0)`);
      tg.addColorStop(1, `rgba(${r},${g},${b},${(opacity * 0.22).toFixed(3)})`);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.strokeStyle = tg;
      ctx.lineWidth   = 1;
      ctx.stroke();
    };

    // Node: accepts optional hover scale (max ~1.10)
    const drawNode = (x, y, tier, hG, pG, scale) => {
      const c    = TIER[tier];
      const glow = Math.min(1, hG + pG);
      const rA   = c.ringA + glow * 0.06;
      const cA   = Math.min(0.95, c.coreA + glow * 0.24);

      const useScale = scale != null && scale !== 1;
      if (useScale) {
        ctx.save();
        ctx.translate(x, y);
        ctx.scale(scale, scale);
        ctx.translate(-x, -y);
      }

      // Outer ring
      ctx.beginPath();
      ctx.arc(x, y, c.ring, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255,255,255,${rA})`;
      ctx.lineWidth   = 0.6;
      ctx.stroke();

      // Radial core
      const cg = ctx.createRadialGradient(x, y, 0, x, y, c.core);
      cg.addColorStop(0,   `rgba(255,255,255,${cA})`);
      cg.addColorStop(0.4, `rgba(255,255,255,${(cA * 0.3).toFixed(3)})`);
      cg.addColorStop(1,   'rgba(255,255,255,0)');
      ctx.beginPath();
      ctx.arc(x, y, c.core, 0, Math.PI * 2);
      ctx.fillStyle = cg;
      ctx.fill();

      // Ambient hover halo
      if (hG > 0.04) {
        const hr = c.ring * 2.8;
        const hg = ctx.createRadialGradient(x, y, c.ring, x, y, hr);
        hg.addColorStop(0, `rgba(255,255,255,${(hG * 0.04).toFixed(3)})`);
        hg.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.beginPath();
        ctx.arc(x, y, hr, 0, Math.PI * 2);
        ctx.fillStyle = hg;
        ctx.fill();
      }

      if (useScale) ctx.restore();
    };

    const drawPulse = (x, y, r, g, b, a) => {
      const pg = ctx.createRadialGradient(x, y, 0, x, y, 11);
      pg.addColorStop(0,   `rgba(${r},${g},${b},${(a * 0.52).toFixed(3)})`);
      pg.addColorStop(0.4, `rgba(${r},${g},${b},${(a * 0.16).toFixed(3)})`);
      pg.addColorStop(1,   `rgba(${r},${g},${b},0)`);
      ctx.beginPath();
      ctx.arc(x, y, 11, 0, Math.PI * 2);
      ctx.fillStyle = pg;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x, y, 1.6, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${r},${g},${b},${Math.min(0.9, a).toFixed(3)})`;
      ctx.fill();
    };

    /* ── Path pulse helper ───────────────────────────────────── */
    // Maps t ∈ [0,1] across an ordered path of node-di values
    // Returns position + segment metadata, or null if nodes are missing
    const getPathPos = (path, t) => {
      const segCount = path.length - 1;
      const raw      = t * segCount;
      const segIdx   = Math.min(Math.floor(raw), segCount - 1);
      const localT   = raw - segIdx;
      const fN = nodes.find(nd => nd.di === path[segIdx]);
      const tN = nodes.find(nd => nd.di === path[segIdx + 1]);
      if (!fN || !tN) return null;
      return {
        x: fN.x + (tN.x - fN.x) * localT,
        y: fN.y + (tN.y - fN.y) * localT,
        localT,
        fromNode: fN,
        toNode:   tN,
      };
    };

    /* ── Render loop ─────────────────────────────────────────── */
    let lastT   = performance.now();
    let elapsed = 0;

    const render = time => {
      const dt = Math.min(time - lastT, 50);
      lastT    = time;
      elapsed += dt;

      ctx.clearRect(0, 0, lw, lh);
      if (raf == null) return;
      if (!nodes.length) { raf = requestAnimationFrame(render); return; }

      // Smooth parallax via CSS transform (no layout cost)
      if (finePtr && !reduced) {
        pxCur.x += (pxTgt.x - pxCur.x) * 0.048;
        pxCur.y += (pxTgt.y - pxCur.y) * 0.048;
        canvas.style.transform = `translate(${pxCur.x.toFixed(2)}px,${pxCur.y.toFixed(2)}px)`;
      }

      pulseG.fill(0);

      const activePulses = [];  // { x, y, rgb, a }
      const activeTrails = [];  // { x1, y1, x2, y2, r, g, b, a }

      if (!reduced) {
        const pDefs = mobile ? [PATH_PULSE_DEFS[0]] : PATH_PULSE_DEFS;

        pDefs.forEach((p, pi) => {
          pState[pi].t = (pState[pi].t + p.speed * dt) % 1;
          const t   = pState[pi].t;
          const pos = getPathPos(p.path, t);
          if (!pos) return;

          // Fade in/out over edge 8% of path to avoid hard pop
          const edge = 0.08;
          let opacity = 1;
          if (t < edge)       opacity = t / edge;
          if (t > 1 - edge)   opacity = (1 - t) / edge;

          // Propagate glow to nodes within 30 px of pulse
          nodes.forEach(nd => {
            const d = Math.hypot(pos.x - nd.x, pos.y - nd.y);
            if (d < 30) pulseG[nd.di] = Math.max(pulseG[nd.di], (1 - d / 30) * opacity * 0.55);
          });

          activePulses.push({ x: pos.x, y: pos.y, rgb: p.rgb, a: opacity * 0.78 });

          // Trail: last 30% of current segment behind the pulse head
          const trailStart = Math.max(0, pos.localT - 0.30);
          const tx1 = pos.fromNode.x + (pos.toNode.x - pos.fromNode.x) * trailStart;
          const ty1 = pos.fromNode.y + (pos.toNode.y - pos.fromNode.y) * trailStart;
          activeTrails.push({
            x1: tx1, y1: ty1, x2: pos.x, y2: pos.y,
            r: p.rgb[0], g: p.rgb[1], b: p.rgb[2], a: opacity,
          });
        });
      }

      // Update hover glow with smooth lerp
      nodes.forEach(nd => {
        const nx = nd.x + pxCur.x;
        const ny = nd.y + pxCur.y;
        const d  = Math.hypot(rawMouse.x - nx, rawMouse.y - ny);
        const tg = finePtr && d < 70 ? Math.max(0, 1 - d / 70) : 0;
        hoverG[nd.di] += (tg - hoverG[nd.di]) * 0.09;
      });

      // 1. Draw static connection lines
      conns.forEach(([ai, bi]) => {
        if (nodes[ai] && nodes[bi]) drawLine(nodes[ai].x, nodes[ai].y, nodes[bi].x, nodes[bi].y);
      });

      // 2. Draw signal trails over the base lines
      activeTrails.forEach(tr => drawTrail(tr.x1, tr.y1, tr.x2, tr.y2, tr.r, tr.g, tr.b, tr.a));

      // 3. Draw nodes — with subtle scale on hover (Option A interaction)
      nodes.forEach((nd, i) => {
        const breatheVal = reduced ? 0.5 : 0.5 + 0.5 * Math.sin(elapsed * 0.00052 + i * 1.35);
        const bG  = breatheVal * TIER[nd.tier].breathe;
        const hVal = hoverG[nd.di];
        const sc  = 1 + hVal * 0.10; // max 1.10× scale at full hover proximity
        drawNode(nd.x, nd.y, nd.tier, hVal * 0.55 + bG, pulseG[nd.di], sc);
      });

      // 4. Draw pulse dots on top
      activePulses.forEach(p => drawPulse(p.x, p.y, p.rgb[0], p.rgb[1], p.rgb[2], p.a));

      raf = requestAnimationFrame(render);
    };

    // Only animate while visible on screen and the tab is in the foreground
    let onScreen = true;
    const start = () => {
      if (raf == null && onScreen && !document.hidden) {
        lastT = performance.now();
        raf = requestAnimationFrame(render);
      }
    };
    const stop = () => { cancelAnimationFrame(raf); raf = null; };
    const io = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      onScreen ? start() : stop();
    });
    io.observe(canvas);
    const onVisibility = () => (document.hidden ? stop() : start());
    document.addEventListener('visibilitychange', onVisibility);

    resize();
    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('mousemove', onMouse, { passive: true });
    start();

    return () => {
      stop();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouse);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    />
  );
};

export default SignalGrid;
